import React, { createContext, useContext, useEffect, useLayoutEffect } from 'react';
import { loadGoogleFont, getFontFamily } from '../utils/fontLoader';

/**
 * Font settings from brand package or theme
 */
export interface BrandFonts {
  headingFont?: string;
  bodyFont?: string;
  baseSize?: string;
}

interface CustomColorsContextValue {
  customColors: Record<string, Record<string, string>> | null;
  fonts: BrandFonts | null;
}

const CustomColorsContext = createContext<CustomColorsContextValue>({
  customColors: null,
  fonts: null,
});

/**
 * Hook to get custom colors from context
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useCustomColors = () => {
  const context = useContext(CustomColorsContext);
  return context.customColors;
};

/**
 * Hook to get brand fonts from context
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useBrandFonts = () => {
  const context = useContext(CustomColorsContext);
  return context.fonts;
};

/**
 * Hook to get both colors and fonts from context
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useBrandStyles = () => {
  const context = useContext(CustomColorsContext);
  return {
    colors: context.customColors,
    fonts: context.fonts,
    getHeadingFontFamily: () => getFontFamily(context.fonts?.headingFont),
    getBodyFontFamily: () => getFontFamily(context.fonts?.bodyFont),
  };
};

interface CustomColorsProviderProps {
  children: React.ReactNode;
  customColors: Record<string, Record<string, string>>;
  fonts?: BrandFonts;
}

export const CustomColorsProvider: React.FC<CustomColorsProviderProps> = ({
  children,
  customColors,
  fonts,
}) => {
  // Auto-load fonts when they change
  useEffect(() => {
    if (fonts?.headingFont) {
      loadGoogleFont(fonts.headingFont);
    }
    if (fonts?.bodyFont && fonts.bodyFont !== fonts.headingFont) {
      loadGoogleFont(fonts.bodyFont);
    }
  }, [fonts?.headingFont, fonts?.bodyFont]);

  // Override theme CSS variables with brand package fonts
  // Use useLayoutEffect to run synchronously after DOM updates
  // and a small timeout to ensure it runs after ThemeProvider's async load
  useLayoutEffect(() => {
    if (!fonts?.headingFont && !fonts?.bodyFont) return;

    const applyFonts = () => {
      if (fonts?.headingFont) {
        document.documentElement.style.setProperty(
          '--theme-heading-font',
          `"${fonts.headingFont}", system-ui, sans-serif`
        );
      }
      if (fonts?.bodyFont) {
        document.documentElement.style.setProperty(
          '--theme-body-font',
          `"${fonts.bodyFont}", system-ui, sans-serif`
        );
      }
    };

    // Apply immediately
    applyFonts();

    // Also apply after a short delay to override any async theme loading
    const timeoutId = setTimeout(applyFonts, 100);

    return () => clearTimeout(timeoutId);
  }, [fonts?.headingFont, fonts?.bodyFont]);

  console.log('🎨 CustomColorsProvider: Received customColors:', customColors);
  console.log('🎨 CustomColorsProvider: Received fonts:', fonts);

  return (
    <CustomColorsContext.Provider value={{ customColors, fonts: fonts || null }}>
      {children}
    </CustomColorsContext.Provider>
  );
};
